import { useEffect, useRef } from "react";

import { Button } from "components/Button/Button";
import { Dialog } from "components/Dialog/Dialog";
import { DialogActionsBar } from "components/Dialog/DialogActionsBar";

import type { ButtonHandle } from "@progress/kendo-react-buttons";
import type { ActionsLayout } from "@progress/kendo-react-dialogs";

interface ConfirmationDialogProps {
  additionalDialogControls?: React.JSX.Element;
  buttonPosition?: ActionsLayout;
  cancelText?: string;
  className?: string;
  confirmText?: string;
  dangerStyleForSubmit?: boolean;
  dialogMessage?: string;
  formattedDialogMessage?: React.JSX.Element;
  onConfirmClick?: () => void;
  preventConfirm?: boolean;
  primaryStyleForSubmit?: boolean;
  testId?: string;
  title: string;
  toggleDialog: () => void;
  visible: boolean;
  autoFocusOnConfirm?: boolean;
}

export const ConfirmationDialog = ({
  additionalDialogControls,
  buttonPosition,
  cancelText,
  className,
  confirmText,
  dangerStyleForSubmit,
  dialogMessage,
  formattedDialogMessage,
  onConfirmClick,
  preventConfirm,
  primaryStyleForSubmit,
  testId,
  title,
  toggleDialog,
  visible,
  autoFocusOnConfirm,
}: ConfirmationDialogProps) => {
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<ButtonHandle | null>(null);

  useEffect(() => {
    if (visible && autoFocusOnConfirm) {
      setTimeout(() => {
        if (confirmRef.current?.element) {
          confirmRef.current.element.focus();
        }
      });
    }
  }, [visible, autoFocusOnConfirm]);

  return (
    <div className="confirmation-dialog" data-testid={testId} ref={dialogContainerRef}>
      {visible && (
        <Dialog
          appendTo={dialogContainerRef.current}
          className={className}
          onClose={toggleDialog}
          title={title}
        >
          {dialogMessage && <p>{dialogMessage}</p>}
          {formattedDialogMessage && <div>{formattedDialogMessage}</div>}
          {additionalDialogControls}
          <DialogActionsBar layout={buttonPosition ?? "stretched"}>
            <Button
              data-testid={testId ? `${testId}-cancel-button` : "dialog-cancel-button"}
              onClick={toggleDialog}
            >
              {cancelText}
            </Button>
            {onConfirmClick && (
              <Button
                className="tc-confirm-btn"
                danger={dangerStyleForSubmit}
                data-testid={testId ? `${testId}-confirm-button` : "dialog-confirm-button"}
                disabled={!!preventConfirm}
                onClick={onConfirmClick}
                themeColor={primaryStyleForSubmit ? "primary" : undefined}
                btnRef={confirmRef}
              >
                {confirmText}
              </Button>
            )}
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};
